import React from 'react';
import { Button } from '../../ui/Button';
import { DEFAULT_APPEARANCE, THEMES, ACCENTS, FONTS, CORNERS, DENSITIES } from '../../../utils/appearance';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const TabStyle = ({ card, updateCard }) => {
  const app = { ...DEFAULT_APPEARANCE, ...(card.appearance || {}) };

  const setAppearance = (key, value) => {
    updateCard('appearance', { ...app, [key]: value });
  };

  const handleReset = () => {
    updateCard('appearance', DEFAULT_APPEARANCE);
  };

  const handleRandomise = () => {
    const themeKeys = Object.keys(THEMES);
    const accentKeys = Object.keys(ACCENTS);
    const fontKeys = Object.keys(FONTS);
    const cornerKeys = Object.keys(CORNERS);
    const densityKeys = Object.keys(DENSITIES);
    const modeKeys = ['light', 'dark'];

    updateCard('appearance', {
      theme: themeKeys[Math.floor(Math.random() * themeKeys.length)],
      accent: accentKeys[Math.floor(Math.random() * accentKeys.length)],
      font: fontKeys[Math.floor(Math.random() * fontKeys.length)],
      corners: cornerKeys[Math.floor(Math.random() * cornerKeys.length)],
      density: densityKeys[Math.floor(Math.random() * densityKeys.length)],
      mode: modeKeys[Math.floor(Math.random() * modeKeys.length)]
    });
  };

  return (
    <div className="space-y-10 pb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
        <div>
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Appearance</h3>
          <p className="text-sm text-slate-500 mt-1">Design your public card instantly.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset}>Reset</Button>
          <Button variant="secondary" size="sm" onClick={handleRandomise}>
            <SafeIcon icon={FiIcons.FiShuffle} className="mr-2" /> Randomise
          </Button>
        </div>
      </div>

      {/* 1. Theme & Mode */}
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-bold text-slate-900">1. Theme & Mode</h4>
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => setAppearance('mode', 'light')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${app.mode === 'light' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}
            >
              Light
            </button>
            <button 
              onClick={() => setAppearance('mode', 'dark')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${app.mode === 'dark' ? 'bg-slate-900 text-white' : 'text-slate-500'}`}
            >
              Dark
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(THEMES).map(([key, theme]) => (
            <button
              key={key}
              onClick={() => setAppearance('theme', key)}
              className={`py-4 px-3 rounded-xl border-2 text-sm font-medium transition-all flex flex-col items-center gap-2 ${app.theme === key ? 'border-slate-900 bg-slate-50 text-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
            >
              <div className={`w-full h-8 rounded-md border ${theme.light.pageBg} ${theme.light.border}`}></div>
              {theme.name}
            </button>
          ))}
        </div>
      </div>

      {/* 2. Accent Color */}
      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-4">2. Accent Colour</h4>
        <div className="flex flex-wrap gap-3">
          {Object.entries(ACCENTS).map(([key, hex]) => (
            <button
              key={key}
              onClick={() => setAppearance('accent', key)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${app.accent === key ? 'ring-2 ring-offset-2 ring-slate-900' : ''}`}
              style={{ backgroundColor: hex }}
              title={key}
            >
              {app.accent === key && <SafeIcon icon={FiIcons.FiCheck} className="text-white" />}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Typography */}
      <div>
        <h4 className="text-sm font-bold text-slate-900 mb-4">3. Typography</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(FONTS).map(([key, font]) => (
            <button
              key={key}
              onClick={() => setAppearance('font', key)}
              className={`py-3 px-3 rounded-xl border-2 text-sm transition-all ${font.class} ${app.font === key ? 'border-slate-900 bg-slate-50 text-slate-900 font-bold' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
            >
              {font.name}
            </button>
          ))}
        </div>
      </div>

      {/* 4. Structure (Corners & Density) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-bold text-slate-900 mb-4">4. Layout Corners</h4>
          <div className="flex bg-slate-100 p-1 rounded-xl">
            {Object.entries(CORNERS).map(([key, corner]) => (
              <button 
                key={key}
                onClick={() => setAppearance('corners', key)}
                className={`flex-1 py-2 text-sm font-medium transition-all ${app.corners === key ? 'bg-white shadow-sm rounded-lg text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {corner.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-bold text-slate-900 mb-4">5. Card Density</h4>
          <div className="flex bg-slate-100 p-1 rounded-xl">
            {Object.entries(DENSITIES).map(([key, density]) => (
              <button 
                key={key}
                onClick={() => setAppearance('density', key)}
                className={`flex-1 py-2 text-sm font-medium transition-all ${app.density === key ? 'bg-white shadow-sm rounded-lg text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {density.name}
              </button>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};